import { useMemo } from "react";
import { Box, Typography, Paper, useTheme } from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ReplyIcon from "@mui/icons-material/Reply";
import PhoneIcon from "@mui/icons-material/Phone";
import ImageIcon from "@mui/icons-material/Image";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import LocationOnIcon from "@mui/icons-material/LocationOn";

import { format } from "@/utils/whatsapp-formatter";

type TemplateButton = {
  type: string;
  text?: string;
  url?: string;
  phone_number?: string;
  [key: string]: unknown;
};

type TemplateComponent = {
  type: string;
  format?: string;
  text?: string;
  example?: {
    header_handle?: string[];
    header_text?: string[];
    body_text?: string[][];
  };
  buttons?: TemplateButton[];
  [key: string]: unknown;
};

type TemplatePreviewFormData = {
  components?: TemplateComponent[];
  exampleVariables?: Array<string | { value: string }>;
  bodyExampleValues?: string[];
  [key: string]: unknown;
};

type TemplatePreviewProps = {
  formData?: TemplatePreviewFormData;
};

// WhatsApp double blue checkmark icon
const BlueDoubleCheckmark = () => (
  <svg
    viewBox="0 0 16 11"
    width="15"
    height="10"
    fill="#53BDEB"
    style={{ display: "inline-block", verticalAlign: "middle", marginLeft: 2 }}
  >
    <path d="M11.15 0.85L5.7 6.3L4.85 5.45C4.65 5.25 4.35 5.25 4.15 5.45C3.95 5.65 3.95 5.95 4.15 6.15L5.35 7.35C5.55 7.55 5.85 7.55 6.05 7.35L11.85 1.55C12.05 1.35 12.05 1.05 11.85 0.85C11.65 0.65 11.35 0.65 11.15 0.85ZM15.15 0.85L9.7 6.3L9.35 5.95C9.15 5.75 8.85 5.75 8.65 5.95C8.45 6.15 8.45 6.45 8.65 6.65L9.35 7.35C9.55 7.55 9.85 7.55 10.05 7.35L15.85 1.55C16.05 1.35 16.05 1.05 15.85 0.85C15.65 0.65 15.35 0.65 15.15 0.85ZM1.15 5.45C0.95 5.65 0.95 5.95 1.15 6.15L3.35 8.35C3.55 8.55 3.85 8.55 4.05 8.35C4.25 8.15 4.25 7.85 4.05 7.65L1.85 5.45C1.65 5.25 1.35 5.25 1.15 5.45Z" />
  </svg>
);

export const TemplatePreview = ({ formData }: TemplatePreviewProps) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  // WhatsApp authentic color palette
  const chatBg = isDark ? "#0B141A" : "#EFEAE2";
  const bubbleBg = isDark ? "#005C4B" : "#D9FDD3";
  const bubbleTextColor = isDark ? "#E9EDEF" : "#111B21";
  const timestampColor = isDark ? "#8696A0" : "#667781";
  const actionButtonColor = isDark ? "#00A884" : "#008069";
  const buttonDividerColor = isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(11, 20, 26, 0.08)";

  const bodyComponent = formData?.components?.find((c: TemplateComponent) => c.type === "BODY");
  const headerComponent = formData?.components?.find((c: TemplateComponent) => c.type === "HEADER");
  const footerComponent = formData?.components?.find((c: TemplateComponent) => c.type === "FOOTER");
  const buttonsComponent = formData?.components?.find(
    (c: TemplateComponent) => c.type === "BUTTONS"
  );

  // Format message time
  const currentTimeString = useMemo(() => {
    try {
      const now = new Date();
      return now.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
    } catch {
      return "12:00 PM";
    }
  }, []);

  if (!formData) {
    return (
      <Paper
        variant="outlined"
        sx={{
          borderRadius: 3,
          p: 3,
          textAlign: "center",
          bgcolor: "background.paper",
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Select a template to view the live WhatsApp preview.
        </Typography>
      </Paper>
    );
  }

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: 380,
        mx: "auto",
        borderRadius: 3,
        overflow: "hidden",
        border: 1,
        borderColor: "divider",
        bgcolor: chatBg,
        backgroundImage: isDark
          ? `radial-gradient(rgba(255, 255, 255, 0.04) 1px, transparent 1px)`
          : `radial-gradient(rgba(0, 0, 0, 0.05) 1px, transparent 1px)`,
        backgroundSize: "16px 16px",
        p: { xs: 2, sm: 2.5 },
        display: "flex",
        flexDirection: "column",
        boxSizing: "border-box",
      }}
    >
      {/* WhatsApp Message Bubble */}
      <Box
        sx={{
          alignSelf: "flex-end",
          width: "fit-content",
          maxWidth: "92%",
          minWidth: 160,
          bgcolor: bubbleBg,
          borderRadius: "7.5px 0px 7.5px 7.5px",
          position: "relative",
          color: bubbleTextColor,
          fontFamily:
            '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
        }}
      >
        {/* Authentic Outgoing WhatsApp Message Tail */}
        <Box
          component="svg"
          viewBox="0 0 8 13"
          sx={{
            position: "absolute",
            top: 0,
            right: -8,
            width: 8,
            height: 13,
            fill: bubbleBg,
          }}
        >
          <path d="M5.188,0H0v11.193l6.467-8.625C7.526,1.156,6.958,0,5.188,0z" />
        </Box>

        {/* Main Bubble Content (Header + Body + Footer + Timestamp) */}
        <Box sx={{ p: "7px 9px 5px 9px" }}>
          {/* 1. Header Component */}
          {headerComponent && (
            <Box sx={{ mb: 0.75 }}>
              {headerComponent.format === "TEXT" && headerComponent.text ? (
                <Typography
                  variant="body2"
                  component="div"
                  sx={{
                    fontWeight: 700,
                    fontSize: "0.9375rem",
                    lineHeight: 1.35,
                    color: bubbleTextColor,
                  }}
                  dangerouslySetInnerHTML={{
                    __html: format(headerComponent.text),
                  }}
                />
              ) : headerComponent.format === "IMAGE" ? (
                <Box
                  sx={{
                    width: "100%",
                    aspectRatio: "16/9",
                    bgcolor: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)",
                    borderRadius: "6px",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 0.5,
                    color: timestampColor,
                  }}
                >
                  <ImageIcon sx={{ fontSize: "2rem" }} />
                  <Typography variant="caption" sx={{ fontSize: "0.72rem", fontWeight: 500 }}>
                    Header Image
                  </Typography>
                </Box>
              ) : headerComponent.format === "VIDEO" ? (
                <Box
                  sx={{
                    width: "100%",
                    aspectRatio: "16/9",
                    bgcolor: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)",
                    borderRadius: "6px",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 0.5,
                    color: timestampColor,
                  }}
                >
                  <PlayCircleOutlineIcon sx={{ fontSize: "2.2rem" }} />
                  <Typography variant="caption" sx={{ fontSize: "0.72rem", fontWeight: 500 }}>
                    Header Video
                  </Typography>
                </Box>
              ) : headerComponent.format === "DOCUMENT" ? (
                <Box
                  sx={{
                    width: "100%",
                    p: 1.25,
                    bgcolor: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)",
                    borderRadius: "6px",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <PictureAsPdfIcon sx={{ fontSize: "1.75rem", color: "#E53935" }} />
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography noWrap variant="body2" sx={{ fontSize: "0.8rem", fontWeight: 600 }}>
                      {headerComponent.text || "Document.pdf"}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ fontSize: "0.7rem", color: timestampColor }}
                    >
                      PDF • 250 KB
                    </Typography>
                  </Box>
                </Box>
              ) : headerComponent.format === "LOCATION" ? (
                <Box
                  sx={{
                    width: "100%",
                    p: 1.25,
                    bgcolor: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)",
                    borderRadius: "6px",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <LocationOnIcon sx={{ fontSize: "1.75rem", color: "#00A884" }} />
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography noWrap variant="body2" sx={{ fontSize: "0.8rem", fontWeight: 600 }}>
                      Location Pin
                    </Typography>
                  </Box>
                </Box>
              ) : (
                <Box
                  sx={{
                    p: 1,
                    bgcolor: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)",
                    borderRadius: "6px",
                    textAlign: "center",
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    {headerComponent.format || "MEDIA"}
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          {/* 2. Body Component */}
          {bodyComponent && (
            <Typography
              variant="body2"
              component="div"
              sx={{
                color: bubbleTextColor,
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                lineHeight: 1.45,
                fontSize: "0.875rem",
                "& strong": {
                  fontWeight: 700,
                },
                "& i, & em": {
                  fontStyle: "italic",
                },
                "& s": {
                  textDecoration: "line-through",
                },
                "& code": {
                  fontFamily: "monospace",
                  backgroundColor: isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.06)",
                  padding: "1px 4px",
                  borderRadius: "3px",
                  fontSize: "0.82rem",
                },
              }}
              dangerouslySetInnerHTML={{
                __html: (() => {
                  let text = bodyComponent.text || "";
                  let sampleValues: string[] = [];

                  if (formData?.exampleVariables && formData.exampleVariables.length > 0) {
                    sampleValues = formData.exampleVariables.map((v: string | { value: string }) =>
                      typeof v === "object" && v !== null && "value" in v ? v.value : String(v)
                    );
                  } else if (
                    formData?.bodyExampleValues ||
                    formData?.components?.find((c: TemplateComponent) => c.type === "BODY")?.example
                      ?.body_text?.[0]
                  ) {
                    sampleValues =
                      formData?.bodyExampleValues ||
                      formData?.components?.find((c: TemplateComponent) => c.type === "BODY")
                        ?.example?.body_text?.[0] ||
                      [];
                  } else {
                    sampleValues = ["Burhanuddin", "50403123", "10:00 AM", "₹1,500", "2024-01-15"];
                  }

                  text = text.replace(/\{\{(\d+)\}\}/g, (match: string, num: string) => {
                    const index = parseInt(num, 10) - 1;
                    return sampleValues?.[index] || match;
                  });

                  return format(text);
                })(),
              }}
            />
          )}

          {/* 3. Footer Component */}
          {footerComponent && footerComponent.text && (
            <Typography
              variant="caption"
              sx={{
                display: "block",
                color: timestampColor,
                fontSize: "0.72rem",
                mt: 0.5,
                lineHeight: 1.3,
              }}
            >
              {footerComponent.text}
            </Typography>
          )}

          {/* 4. Timestamp & Double Checkmarks (Read Receipts) */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              gap: 0.4,
              mt: 0.25,
              float: "right",
              ml: 1.5,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                fontSize: "0.6875rem",
                color: timestampColor,
                fontWeight: 400,
                lineHeight: 1,
              }}
            >
              {currentTimeString}
            </Typography>
            <BlueDoubleCheckmark />
          </Box>
          <Box sx={{ clear: "both" }} />
        </Box>

        {/* 5. Interactive Action Buttons (Attached directly under message bubble) */}
        {buttonsComponent?.buttons && buttonsComponent.buttons.length > 0 && (
          <Box
            sx={{
              borderTop: `1px solid ${buttonDividerColor}`,
              display: "flex",
              flexDirection: "column",
              bgcolor: isDark ? "#111B21" : "#FFFFFF",
              borderBottomLeftRadius: "7.5px",
              borderBottomRightRadius: "7.5px",
              overflow: "hidden",
            }}
          >
            {buttonsComponent.buttons.map((button: TemplateButton, index: number) => (
              <Box
                key={index}
                sx={{
                  borderTop: index > 0 ? `1px solid ${buttonDividerColor}` : "none",
                  py: 1.1,
                  px: 1.5,
                  minHeight: 40,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 0.75,
                  color: actionButtonColor,
                  cursor: "pointer",
                  userSelect: "none",
                  transition: "background-color 0.15s ease",
                  "&:hover": {
                    bgcolor: isDark ? "rgba(255, 255, 255, 0.06)" : "rgba(0, 0, 0, 0.03)",
                  },
                  "&:active": {
                    bgcolor: isDark ? "rgba(255, 255, 255, 0.12)" : "rgba(0, 0, 0, 0.07)",
                  },
                }}
              >
                {button.type === "QUICK_REPLY" && (
                  <ReplyIcon sx={{ fontSize: "1.05rem", color: actionButtonColor }} />
                )}
                {button.type === "URL" && (
                  <OpenInNewIcon sx={{ fontSize: "1.05rem", color: actionButtonColor }} />
                )}
                {button.type === "PHONE_NUMBER" && (
                  <PhoneIcon sx={{ fontSize: "0.95rem", color: actionButtonColor }} />
                )}

                <Typography
                  variant="body2"
                  sx={{
                    fontSize: "0.875rem",
                    fontWeight: 500,
                    color: actionButtonColor,
                    textAlign: "center",
                    lineHeight: 1.2,
                  }}
                >
                  {button.type === "QUICK_REPLY"
                    ? button.text
                    : button.type === "URL"
                      ? button.text || "Visit Link"
                      : button.type === "PHONE_NUMBER"
                        ? button.text || button.phone_number
                        : button.text || button.url || ""}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
};
